/**
 * v77.99.5 — Admin Phase 5 components.
 *
 * Three small modals + one inline GDPR-controls block, used from the
 * Admin user list (AdminAccessSection) so each row can launch:
 *   • AccountTimelineModal — chronological view of a single user's
 *     account events (account_created, email_verified, trial events,
 *     subscription events, child events, exports, admin actions).
 *     Filter by event-type + date range. Newest first by default.
 *   • UserGdprControls — "Data controls" inline block: Export user
 *     data (downloads JSON), Soft-delete (archive, reversible),
 *     Restore. Every action is audit-logged on the backend.
 *
 * Kept in a single file because the three controls share the user-row
 * context and styling tokens; splitting into per-modal files would
 * require duplicating the token palette + the same loading-state
 * scaffolding.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Calendar as CalendarIcon, Download, Archive, RotateCcw, Search,
  RefreshCw, ChevronDown, ChevronUp,
} from "lucide-react";


/* ---------- AccountTimelineModal ---------- */

const EVENT_LABELS = {
  account_created: "Account created",
  email_verified: "Email verified",
  trial_started: "Trial started",
  trial_ending_soon: "Trial ending soon (warning)",
  trial_ended: "Trial ended",
  subscription_started: "Subscription started",
  subscription_renewed: "Subscription renewed",
  subscription_cancelled: "Subscription cancelled",
  subscription_past_due: "Subscription past due",
  premium_granted: "Premium granted (admin)",
  premium_revoked: "Premium revoked (admin)",
  last_login: "Last sign-in",
  child_created: "Child profile created",
  expense_created: "Expense logged",
  export_failed: "Export failed",
  export_complete: "Export completed",
  admin_action: "Admin action",
  resend_email: "Email resent (admin)",
  gdpr_archive: "GDPR archive (admin)",
  gdpr_restore: "GDPR restore (admin)",
  export_data: "Data exported (admin)",
};

const EVENT_TONES = {
  email_verified: "green",
  trial_started: "amber",
  trial_ending_soon: "amber",
  trial_ended: "amber",
  subscription_started: "green",
  subscription_renewed: "green",
  subscription_past_due: "red",
  premium_granted: "violet",
  premium_revoked: "red",
  export_failed: "red",
  export_complete: "green",
  gdpr_archive: "red",
  gdpr_restore: "violet",
  export_data: "violet",
  admin_action: "violet",
  resend_email: "violet",
};

export function AccountTimelineModal({ user, open, onClose }) {
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);
  const [eventFilter, setEventFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    if (!open || !user?.id) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/admin/users/${user.id}/timeline?limit=200`);
        if (cancelled) return;
        setEvents(data.events || []);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Couldn't load timeline");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, user?.id]);

  const eventTypes = useMemo(() => {
    const set = new Set(events.map((e) => e.type));
    return Array.from(set).sort();
  }, [events]);

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (eventFilter !== "all" && e.type !== eventFilter) return false;
      if (from && (e.ts || "") < from) return false;
      if (to && (e.ts || "") > to + "T23:59:59") return false;
      return true;
    });
  }, [events, eventFilter, from, to]);

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl bg-white max-h-[85vh] overflow-y-auto"
        data-testid="account-timeline-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-[#2E3A3F]">Account timeline</DialogTitle>
          <DialogDescription className="text-[#6F828A]">
            {user?.name || user?.email} — newest events first. {events.length} events loaded.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 pt-2 pb-1 border-b border-[#E2E8EC]">
          <select
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className="h-8 text-[12px] border border-[#D9E1E3] rounded-sm px-2"
            data-testid="timeline-filter-type"
          >
            <option value="all">All event types</option>
            {eventTypes.map((t) => (
              <option key={t} value={t}>{EVENT_LABELS[t] || t}</option>
            ))}
          </select>
          <Input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-8 text-[12px] w-auto"
            placeholder="From"
            data-testid="timeline-filter-from"
          />
          <Input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-8 text-[12px] w-auto"
            placeholder="To"
            data-testid="timeline-filter-to"
          />
          <span className="text-[11px] text-[#6F828A] ml-auto">{filtered.length} shown</span>
        </div>

        {busy ? (
          <div className="text-sm text-[#6F828A] py-6 text-center">Loading timeline…</div>
        ) : (
          <ol className="divide-y divide-[#E2E8EC] mt-2" data-testid="timeline-event-list">
            {filtered.length === 0 ? (
              <li className="text-sm text-[#6F828A] py-6 text-center list-none">
                No events match these filters.
              </li>
            ) : (
              filtered.map((e, idx) => (
                <TimelineRow key={`${e.type}-${e.ts}-${idx}`} event={e} />
              ))
            )}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TimelineRow({ event }) {
  const [open, setOpen] = useState(false);
  const tone = EVENT_TONES[event.type] || "grey";
  const toneCls = {
    green: "bg-[#E5F1EA] text-[#1E5A1E] border-[#A9D3A9]",
    amber: "bg-[#FDF4E2] text-[#8F6B1F] border-[#E6CFA2]",
    red:   "bg-[#FBEFED] text-[#8C2A22] border-[#E8B8B2]",
    violet:"bg-[#ECE3F3] text-[#4E3877] border-[#C5B2DE]",
    grey:  "bg-[#F0EFEE] text-[#6F828A] border-[#CFD6D8]",
  }[tone];
  const hasMeta = event.meta && Object.keys(event.meta).length > 0;
  return (
    <li className="py-2.5">
      <button
        type="button"
        onClick={() => hasMeta && setOpen(!open)}
        className={`w-full text-left flex items-baseline gap-2 ${hasMeta ? "cursor-pointer hover:bg-[#F8FAFB]" : "cursor-default"} rounded-sm px-1 -mx-1 transition-colors`}
      >
        <span className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border ${toneCls}`}>
          {EVENT_LABELS[event.type] || event.type}
        </span>
        <span className="text-[12.5px] text-[#2E3A3F] flex-1 truncate">
          {event.summary || "—"}
        </span>
        <span className="text-[10px] text-[#6F828A] font-mono-data">{formatDT(event.ts)}</span>
        {hasMeta && (open ? (
          <ChevronUp className="w-3.5 h-3.5 text-[#6F828A]" />
        ) : (
          <ChevronDown className="w-3.5 h-3.5 text-[#6F828A]" />
        ))}
      </button>
      {hasMeta && open && (
        <pre className="mt-1.5 text-[11px] bg-[#F8FAFB] border border-[#E2E8EC] rounded-sm p-2 overflow-x-auto text-[#2E3A3F]">
          {JSON.stringify(event.meta, null, 2)}
        </pre>
      )}
    </li>
  );
}


/* ---------- UserGdprControls (inline expandable block) ---------- */

export function UserGdprControls({ user, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isArchived = (user.account_status || "active") === "archived";

  const handleExport = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/users/${user.id}/export-data`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `coparent-console-export-${user.email || user.id}-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("User data exported");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const handleArchive = async () => {
    const reason = window.prompt(`Archive (soft-delete) ${user.email}?\n\nThis is reversible — the account is hidden but data is preserved. Provide a brief reason for the audit log:`, "");
    if (reason === null) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/gdpr-archive`, { reason });
      toast.success(`${user.email} archived (reversible)`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRestore = async () => {
    if (!window.confirm(`Restore ${user.email}? They'll regain access immediately.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/gdpr-restore`);
      toast.success(`${user.email} restored`);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Restore failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <details
      className="mt-2 group"
      data-testid={`admin-gdpr-controls-${user.id}`}
    >
      <summary className="cursor-pointer text-[11px] uppercase tracking-[0.1em] text-[#6F828A] hover:text-[#2E3A3F] select-none flex items-center gap-1.5">
        <ChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
        Data controls (GDPR)
      </summary>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleExport}
          disabled={busy}
          className="h-8 text-[12px] border-[#C5B2DE] text-[#4E3877] hover:bg-[#ECE3F3] rounded-sm"
          data-testid={`admin-gdpr-export-${user.id}`}
          title="Download a JSON dump of every record this user owns or is scoped to (GDPR Article 15)."
        >
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export data
        </Button>
        {!isArchived ? (
          <Button
            type="button"
            variant="outline"
            onClick={handleArchive}
            disabled={busy}
            className="h-8 text-[12px] border-[#E8B8B2] text-[#8C2A22] hover:bg-[#FBEFED] rounded-sm"
            data-testid={`admin-gdpr-archive-${user.id}`}
            title="Soft-delete: hide the account but keep data. Reversible via Restore."
          >
            <Archive className="w-3.5 h-3.5 mr-1.5" /> Soft-delete
          </Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            onClick={handleRestore}
            disabled={busy}
            className="h-8 text-[12px] border-[#A9D3A9] text-[#1E5A1E] hover:bg-[#E5F1EA] rounded-sm"
            data-testid={`admin-gdpr-restore-${user.id}`}
            title="Restore an archived account."
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restore
          </Button>
        )}
      </div>
    </details>
  );
}
